import { Link } from 'react-router-dom';
import { Smartphone, ArrowRight } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { useMember, usePublicConfig, useContribution } from '../context';
import { useMemo } from 'react';

const currencyFormatter = new Intl.NumberFormat('en-KE', {
  style: 'currency',
  currency: 'KES',
  maximumFractionDigits: 0,
});

const formatCurrency = (value: number) => currencyFormatter.format(value);

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(iso));
};

export const MemberDashboardPage = () => {
  const { dashboard, dashboardLoading, dashboardError, nextPayout } = useMember();
  const { config: publicConfig } = usePublicConfig();
  const { currentContribution: contributionStatus } = useContribution();

  const chamaName = publicConfig?.name ?? 'LoopFund';

  const currentContribution = dashboard?.current_contribution;
  const user = dashboard?.user;
  const config = dashboard?.chama_config;

  const contributions = useMemo(() => {
    return (dashboard?.contribution_history ?? []).slice(0, 3).map((c) => ({
      month: c.month,
      status: c.status,
      amount: formatCurrency(c.amount),
      date: formatDate(c.created_at),
    }));
  }, [dashboard]);

  const penalties = useMemo(() => {
    return (dashboard?.penalties.unpaid ?? []).map((p) => ({
      reason: `Late contribution - ${p.month}`,
      amount: formatCurrency(p.amount),
      status: p.is_paid ? `Cleared ${formatDate(p.created_at)}` : 'Outstanding',
    }));
  }, [dashboard]);

  const payouts = useMemo(() => {
    return (dashboard?.payout_history ?? []).map((p) => ({
      id: p.id,
      user_id: p.user_id,
      user_name: p.user_name || 'Unknown Member',
      month: p.month,
      cycle: p.cycle,
      amount: formatCurrency(p.amount),
      status: p.status === 'paid' ? 'Completed' : p.status === 'pending' ? 'Pending' : 'Failed',
      date_paid: formatDate(p.date_paid),
      is_current_user: p.user_id === user?.id,
    }));
  }, [dashboard, user]);

  if (dashboardLoading && !dashboard) {
    return (
      <AnimatedSection className="section-padding">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <div className="h-12 w-12 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
            <p className="text-sm font-medium text-slate-500 dark:text-slate-300">
              Loading your dashboard...
            </p>
          </div>
        </div>
      </AnimatedSection>
    );
  }

  if (dashboardError) {
    return (
      <AnimatedSection className="section-padding">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-4">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
            <h2 className="font-display text-xl">Failed to load dashboard</h2>
            <p className="mt-2 text-sm">{dashboardError}</p>
          </div>
        </div>
      </AnimatedSection>
    );
  }

  if (!dashboard || !user || !config) {
    return (
      <AnimatedSection className="section-padding">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-center py-20">
          <p className="text-sm text-slate-500 dark:text-slate-300">No dashboard data available.</p>
        </div>
      </AnimatedSection>
    );
  }

  return (
  <AnimatedSection className="section-padding">
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 lg:flex-row">
      <aside className="order-1 flex w-full flex-col gap-6 rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 lg:order-1 lg:sticky lg:top-28 lg:h-fit lg:w-80">
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-brand-700 dark:text-brand-300">Welcome back</p>
          <h1 className="mt-2 font-display text-2xl text-slate-900 dark:text-white">{user.full_name}</h1>
          <p className="mt-2 text-sm">
            Member ID: {user.id} • Joined {formatDate(user.date_joined)}
          </p>
        </div>
        <div className="space-y-3">
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">This month</p>
            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
              {currentContribution?.status === 'paid'
                ? `Contribution recorded on ${formatDate(currentContribution.created_at)}`
                : `Contribution due by ${currentContribution?.due_date ?? '—'}`}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Your payout turn</p>
            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
              Cycle {config.current_cycle} • Position {nextPayout?.position_in_queue ?? '—'}
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Estimate: {nextPayout?.estimated_months_to_payout ? `${nextPayout.estimated_months_to_payout} months` : '—'}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Need help?</p>
            <p className="mt-1">hello@{chamaName.toLowerCase().replace(' ', '')}.com • +254 700 111 222</p>
          </div>
        </div>
        <Link
          to="/apply"
          className="inline-flex items-center justify-center rounded-full bg-brand-600 px-6 py-3 font-semibold text-white shadow-sm transition hover:bg-brand-700"
        >
          Refer a friend
        </Link>
      </aside>

      <div className="order-2 flex w-full flex-col gap-8 lg:order-2">
        {!config?.contributions_open && (
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6 dark:border-blue-500/40 dark:bg-blue-500/10">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-blue-600 p-2 dark:bg-blue-700">
                <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-blue-900 dark:text-blue-100">
                  Contributions are currently closed
                </p>
                <p className="mt-1 text-sm text-blue-700 dark:text-blue-200">
                  {config.cycle_state === 'setup' && 'The admin is setting up the next cycle. You will be notified when contributions open.'}
                  {config.cycle_state === 'payout_ready' && 'Contributions have closed for this period. Payouts are being processed.'}
                  {config.cycle_state === 'cycle_active' && 'Cycle is active. Contributions will open for the next period soon.'}
                  {config.cycle_state === 'paused' && 'Contributions are temporarily paused.'}
                </p>
              </div>
            </div>
          </div>
        )}
        {contributionStatus && contributionStatus.status !== 'paid' && config?.contributions_open && (
          <Link
            to="/contribution"
            className="group relative overflow-hidden rounded-2xl border border-brand-200 bg-gradient-to-r from-brand-50 to-brand-100 p-6 shadow-sm transition hover:shadow-md dark:border-brand-800/50 dark:from-brand-900/30 dark:to-brand-800/30"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="rounded-full bg-brand-600 p-3 dark:bg-brand-700">
                  <Smartphone className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.35em] text-brand-700 dark:text-brand-300">
                    {contributionStatus.status === 'overdue' || contributionStatus.status === 'penalized'
                      ? 'Overdue payment'
                      : 'Monthly contribution'}
                  </p>
                  <h3 className="mt-1 text-xl font-semibold text-slate-900 dark:text-white">
                    Pay {formatCurrency(contributionStatus.amount)} via M-Pesa
                  </h3>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    {contributionStatus.due_date ? `Due ${contributionStatus.due_date}` : contributionStatus.month}
                  </p>
                </div>
              </div>
              <ArrowRight className="h-6 w-6 text-brand-600 transition-transform group-hover:translate-x-1 dark:text-brand-300" />
            </div>
          </Link>
        )}

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Next payout</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">
              {nextPayout?.position_in_queue ? `Position ${nextPayout.position_in_queue}` : 'Pending'}
            </p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {nextPayout?.has_received_this_cycle
                ? 'You have received your payout for this cycle.'
                : "We will notify you once it's your turn."}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Cycle summary</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">
              {formatCurrency(config.contribution_amount)}
            </p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              Monthly contribution for Cycle {config.current_cycle}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Penalties owed</p>
            <p className="mt-1 text-2xl font-semibold text-brand-600 dark:text-brand-300">
              {formatCurrency(dashboard.penalties.total_unpaid)}
            </p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {dashboard.penalties.total_unpaid === 0
                ? 'Great! You have no outstanding penalties.'
                : `${dashboard.penalties.unpaid.length} outstanding ${dashboard.penalties.unpaid.length === 1 ? 'penalty' : 'penalties'}`}
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-slate-900 dark:text-white">Your contributions</h2>
            <Link 
              to="/history"
              className="flex items-center gap-1 text-sm font-semibold text-brand-600 transition hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
            >
              View All
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
              <thead className="text-left text-xs uppercase tracking-[0.25em] text-slate-400 dark:text-slate-500">
                <tr>
                  <th className="px-0 py-2">Month</th>
                  <th className="px-0 py-2">Status</th>
                  <th className="px-0 py-2">Amount</th>
                  <th className="px-0 py-2">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-sm text-slate-700 dark:divide-slate-800 dark:text-slate-200">
                {contributions.length > 0 ? (
                  contributions.map((item) => (
                    <tr key={item.month}>
                      <td className="px-0 py-3">{item.month}</td>
                      <td className="px-0 py-3">
                        <span
                          className={
                            item.status.toLowerCase() === 'paid'
                              ? 'rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-200'
                              : item.status.toLowerCase() === 'failed'
                              ? 'rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400'
                              : 'rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200'
                          }
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="px-0 py-3 font-semibold">{item.amount}</td>
                      <td className="px-0 py-3">{item.date}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-0 py-6 text-center text-slate-500 dark:text-slate-400">
                      No contributions yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
            <h2 className="font-display text-xl text-slate-900 dark:text-white">Penalty history</h2>
            {penalties.length === 0 ? (
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">You have no penalties on record.</p>
            ) : (
              <ul className="mt-4 space-y-3 text-sm text-slate-600 dark:text-slate-300">
                {penalties.map((penalty) => (
                  <li
                    key={penalty.reason}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/70"
                  >
                    <p className="font-semibold text-slate-900 dark:text-white">{penalty.reason}</p>
                    <div className="mt-1 flex items-center justify-between">
                      <span>{penalty.amount}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">{penalty.status}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
            <h2 className="font-display text-xl text-slate-900 dark:text-white">Payout history</h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Recent payouts in the chama rotation</p>
            {payouts.length === 0 ? (
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">No payouts have been made yet.</p>
            ) : (
              <ul className="mt-4 space-y-3 text-sm text-slate-600 dark:text-slate-300">
                {payouts.map((payout) => (
                  <li
                    key={payout.id}
                    className={`rounded-xl border px-4 py-3 ${
                      payout.is_current_user
                        ? 'border-brand-200 bg-brand-50 dark:border-brand-800/50 dark:bg-brand-900/20'
                        : 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/70'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900 dark:text-white">
                          {payout.user_name}
                          {payout.is_current_user && (
                            <span className="ml-2 text-xs text-brand-600 dark:text-brand-400">(You)</span>
                          )}
                        </p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Cycle {payout.cycle} · {payout.month}
                        </p>
                      </div>
                      <span
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                          payout.status === 'Completed'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-200'
                            : payout.status === 'Pending'
                            ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200'
                            : 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-400/30 dark:bg-rose-500/10 dark:text-rose-200'
                        }`}
                      >
                        {payout.status}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2 dark:border-slate-700">
                      <span className="font-semibold text-slate-900 dark:text-white">{payout.amount}</span>
                      {payout.date_paid !== '—' && (
                        <span className="text-xs text-slate-500 dark:text-slate-400">Paid {payout.date_paid}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  </AnimatedSection>
  );
};
